/**
 * The receipt a verifier's phone writes after each scan, for the history.
 *
 * It is written offline, like the verification, signed by this phone's device
 * key and queued in IndexedDB; `sync.ts` uploads the queue whenever there is a
 * network. A receipt is evidence the verifier signs about what it saw, so it
 * names the person the credential claims to be even when the verification
 * refused them: someone showing a forged card in your name is exactly what you
 * want in your history.
 */
import { ATTR, RECEIPT_QUEUE_MAX } from "./constants";
import { signWithDevice } from "./crypto";
import {
  decodeAttributes, decodeCredential, decodePresentation, encodeReceipt, receiptSigningInput,
} from "./format";
import * as storage from "./storage";

/** Everything a receipt says except when and the signature. */
export interface ReceiptDraft {
  subject: string;
  name: string;
  /** "ok" or the reason the verifier was shown. */
  result: string;
  mode: number;
  nonce: Uint8Array;
}

export type ReceiptStore = Pick<
  typeof storage, "loadDeviceKey" | "correctedNow" | "updateReceiptQueue"
>;

const b64 = (raw: Uint8Array) =>
  btoa(String.fromCharCode(...raw)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

/**
 * What the receipt for this scan names, or `null` when the bytes are not a
 * presentation carrying a credential: a poster has no subject to attribute it
 * to, and the spec writes no receipt for it.
 *
 * Read from the scanned bytes here rather than handed over by
 * `verifyPresentation`, whose refusals carry the reason and nothing else so a
 * refused name never reaches the result screen. This decode reads the same
 * bytes, and what it returns goes into a signed receipt, not onto a screen.
 */
export function receiptDraft(payload: Uint8Array, result: string): ReceiptDraft | null {
  try {
    const presentation = decodePresentation(payload);
    const attributes = decodeAttributes(decodeCredential(presentation.credentialBytes).attributesBytes);
    const text = (key: number) => {
      const value = attributes.get(key);
      return typeof value === "string" ? value : "";
    };
    return {
      subject: text(ATTR.ID),
      name: text(ATTR.NAME),
      result,
      mode: presentation.mode,
      nonce: presentation.nonce,
    };
  } catch {
    return null;
  }
}

/**
 * Signs the receipt at the corrected clock and appends it to the queue,
 * dropping the oldest past `RECEIPT_QUEUE_MAX`. A phone with no device key has
 * nothing to sign with, and one with no corrected clock has no time the
 * receipt could honestly carry; both skip it.
 */
export async function recordReceipt(
  draft: ReceiptDraft, store: ReceiptStore = storage,
): Promise<void> {
  const pair = await store.loadDeviceKey();
  const { now, synced } = await store.correctedNow();
  if (!pair || !synced) return;
  // Empty in this version. The field is in the format so a note typed by the
  // verifier later is not a format change.
  const fields = { ...draft, time: now, note: "" };
  const signature = await signWithDevice(pair.privateKey, receiptSigningInput(fields));
  const receipt = b64(encodeReceipt({ ...fields, signature }));
  await store.updateReceiptQueue((queue) => [...queue, receipt].slice(-RECEIPT_QUEUE_MAX));
}
